import {
  AiAttachmentInfo,
  ApdmBuildChecklist,
  ApdmBuildStep,
  ApdmCampaignMonitor,
  ApdmPlanSettledFacts,
  ApdmStepAnswer,
} from '@euler/model/apdm';
import { BiAnswer } from '@euler/model/bi/answer';
import { createContext, useContext } from 'react';
import { NEVER, Observable } from 'rxjs';
import type { AiChatTap } from '../conversation';

/** What a card in the conversation may do to the conversation itself. */
export interface AiChatActions {
  /**
   * Sends a user message, as if typed; false when a reply is running. A
   * build card sends its tapped `answer` with it, so the server settles the
   * step before the reply; a BI widget sends its `biAnswer` the same way.
   */
  send: (
    text: string,
    extra?: {
      answer?: ApdmStepAnswer;
      biAnswer?: BiAnswer;
      attachments?: AiAttachmentInfo[];
    },
  ) => boolean;
  /** A reply is streaming: cards hold their actions until it ends. */
  busy: boolean;
  /** Puts the cursor in the composer, for "adjust it in words". */
  focusComposer: () => void;
  /** Each tap whose reply ended, so a card can read its state again. */
  tapSettled$: Observable<AiChatTap>;
}

const kDetached: AiChatActions = {
  send: () => false,
  busy: false,
  focusComposer: () => undefined,
  tapSettled$: NEVER,
};

export const AiChatActionsContext = createContext<AiChatActions>(kDetached);

export function useAiChatActions(): AiChatActions {
  return useContext(AiChatActionsContext);
}

/**
 * Whether the card sits in a conversation it can answer in: the 知车 AI
 * chat or the floating assistant. A widget rendered anywhere else has
 * nowhere to send a tap.
 */
export function useAiChatAttached(): boolean {
  return useContext(AiChatActionsContext) !== kDetached;
}

/** Facts about the message a card sits in. */
export interface AiChatMessageInfo {
  /** The latest reply; its questions are the ones still asked. */
  latest: boolean;
  /**
   * The build steps whose newest card is in this reply. A side question
   * does not take a step's card away: the newest card of the open step
   * still takes the answer, wherever it is.
   */
  answers: readonly ApdmBuildStep[];
  /**
   * The file uploads whose newest `bi_upload` card is in this reply: only
   * that card takes the upload's taps, wherever it is in the conversation.
   */
  biUploads: readonly string[];
}

export const AiChatMessageContext = createContext<AiChatMessageInfo>({
  latest: true,
  answers: [],
  biUploads: [],
});

export function useAiChatMessage(): AiChatMessageInfo {
  return useContext(AiChatMessageContext);
}

/** The campaign this conversation launched, live; null before a launch. */
export const AiChatCampaignContext = createContext<ApdmCampaignMonitor | null>(
  null,
);

/** The conversation's open campaign build, as the server last folded it. */
export const AiChatPlanContext = createContext<ApdmBuildChecklist | null>(null);

/** What the build has settled, for cards whose defaults depend on it. */
export const AiChatSettledContext = createContext<ApdmPlanSettledFacts | null>(
  null,
);

/**
 * Whether a build card of this step takes input: only the step's newest
 * card, and only while its step is the one still open (spec §11.4.2).
 */
export function useAiPlanStepOpen(step: ApdmBuildStep): boolean {
  const chat = useAiChatActions();
  const { answers } = useAiChatMessage();
  const checklist = useContext(AiChatPlanContext);
  return (
    answers.includes(step) && !chat.busy && checklist?.currentStep === step
  );
}
